import Lenis from "lenis";
import { useEffect, useRef } from "react";

import { usePath } from "@/router";

/**
 * Smooth scrolling, and nav links that never put a hash in the address bar.
 *
 * Two jobs in one island because they are the same job: a click on the nav
 * should move the page, and moving the page is not navigating.
 *
 * Adapted from the same component on Alderbill, including the two bugs it
 * documents — both cost real time to find and neither is obvious from the
 * symptom, so the reasoning is kept rather than re-derived.
 *
 * ── Why a library and not `scroll-behavior: smooth` ────────────────────────
 *
 * The CSS one-liner is genuinely the right answer for most pages. It loses on
 * two counts here.
 *
 * **Nothing about it is tunable.** Duration and easing belong to the browser,
 * they differ between browsers, and Chrome covers the distance from the hero
 * to the pricing band fast enough to read as a jump cut rather than a scroll.
 * There is no property to slow it down.
 *
 * **It would fight the route transition.** Scroll is reset to the top on
 * every client navigation (below). With a smooth root, clicking through to
 * /contact would animate the *outgoing* page up to the top before the new one
 * appeared, which reads as the site losing its place.
 *
 * So the CSS rule is absent rather than layered under this, and it has to be:
 * Lenis drives the page by calling `window.scrollTo` every frame, and a smooth
 * root would ask the browser to *animate to* each of those frames. The two
 * smoothings fight and the result is worse than neither.
 *
 * ── What is deliberate ─────────────────────────────────────────────────────
 *
 * **The links stay real anchors in the server-rendered markup.** A crawler
 * follows them, a middle-click opens them, and with JavaScript off they still
 * jump to the right band — `scroll-margin-top` in keglam.css keeps the sticky
 * nav off the heading on that path. This only improves the ordinary left
 * click.
 *
 * **The address bar is left alone**, which is the whole point. A default
 * anchor click writes `/#services` into the URL, where it sits, and then gets
 * copied into a shared link. Somebody who scrolled down a page has not gone
 * anywhere, and the URL should not claim they have.
 *
 * **Lenis's own `anchors` option is left off** precisely because its version
 * does the one thing this component exists to prevent.
 *
 * **Reduced motion needs no code here.** Lenis's `respectReducedMotion`
 * defaults to true: smoothing off, programmatic scrolls instant.
 */

/** Breathing room between the sticky nav and the heading it scrolls to. */
const GAP = 14;

/**
 * How much of the remaining distance is covered each frame while scrolling by
 * wheel or trackpad. **This is the feel dial.** Lenis ships 0.1; this is a
 * little tighter, because the page sells an unhurried second opinion but a
 * visitor still expects the page to arrive when they ask it to.
 */
const LERP = 0.12;

/** Seconds for a nav click, whatever the distance. */
const ANCHOR_DURATION = 1.1;

export function SmoothScroll() {
  const path = usePath();
  const lenisRef = useRef<Lenis | null>(null);

  useEffect(() => {
    const lenis = new Lenis({
      lerp: LERP,
      // Lenis runs its own frame loop; no useRef/rAF plumbing to get wrong.
      autoRaf: true,
      // Clicking through to /contact mid-glide should not leave the page
      // coasting as the next route mounts.
      stopInertiaOnNavigate: true,
    });

    lenisRef.current = lenis;

    function onClick(event: MouseEvent) {
      // Anything that is not a plain left click is the visitor asking for the
      // browser's behaviour — a new tab, a saved link — so stay out of it.
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      const target = event.target;
      if (!(target instanceof Element)) return;
      const link = target.closest("a");
      if (link === null) return;
      if (link.target !== "" && link.target !== "_self") return;

      // `getAttribute` rather than `link.hash`, so a link to another page that
      // happens to carry a fragment is left to navigate normally.
      const href = link.getAttribute("href");
      if (href === null || !href.startsWith("#") || href.length < 2) return;

      const section = document.getElementById(href.slice(1));
      if (section === null) return;

      event.preventDefault();

      /*
       * Measured, not hardcoded. The nav is sticky, so scrolling a heading to
       * the top of the viewport parks it underneath. Its height changes at the
       * breakpoint where the links drop out, and a hardcoded number would be
       * wrong on exactly one layout and go unnoticed for months.
       */
      const nav = document.querySelector(".k-nav");
      const offset = nav instanceof HTMLElement ? nav.offsetHeight + GAP : GAP;

      /*
       * An absolute position rather than the element, on purpose.
       *
       * Handed an element, Lenis resolves it *and subtracts its computed
       * `scroll-margin-top`* — keglam.css sets that to 96px for the no-JS
       * path. Passing the element plus our measured offset therefore applies
       * the nav clearance twice and parks every section about 96px too low,
       * leaving a dead band of paper under the nav. Handed a number, Lenis
       * skips that branch and scrolls exactly where it is told.
       *
       * The two clearances stay in their own lanes: `scroll-margin-top` for
       * the browser's own scrolling, this measurement for ours.
       */
      const top = Math.max(
        0,
        section.getBoundingClientRect().top + window.scrollY - offset,
      );

      lenis.scrollTo(top, {
        duration: ANCHOR_DURATION,
        /*
         * Focus moves only once the page has stopped.
         *
         * Doing it up front cancels the scroll outright in Chromium.
         * `preventScroll` promises that focusing will not *cause* a scroll; it
         * does not stop focusing from killing one already in flight. The
         * symptom is a lurch, and it is the reason this file is worth reading
         * before editing.
         *
         * It has to happen at all: without it a keyboard user activates
         * "Services", the page moves, and their next Tab picks up back in the
         * nav — so they tab through the whole page to reach the section they
         * just asked for.
         */
        onComplete: () => {
          section.setAttribute("tabindex", "-1");
          section.focus({ preventScroll: true });
        },
      });
    }

    document.addEventListener("click", onClick);
    return () => {
      document.removeEventListener("click", onClick);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  /*
   * A new page starts at the top.
   *
   * The browser only does this for real navigations; a pushState is not one,
   * so without this, clicking Contact from halfway down the home page lands
   * you halfway down a shorter page — usually staring at the footer.
   *
   * It has to go through Lenis rather than `window.scrollTo`, because Lenis
   * holds its own idea of the current position and drives the page from it
   * every frame. Setting the scroll behind its back gets smoothly undone.
   *
   * The first run is skipped: on a fresh load the browser is either at the
   * top already or restoring a position the visitor left, and overriding that
   * is the bug this exists to prevent, pointed the other way.
   */
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    lenisRef.current?.scrollTo(0, { immediate: true });
  }, [path]);

  return null;
}
